import { gzipSync } from "fflate";
import { useCallback } from "react";

/**
 * Normally, one would use the 'Content-Encoding' header to indicate compression.
 * However, this causes API Gateway to decompress the body and send the decompressed
 * data to the integration, which is not what we want.
 * 
 * Therefore, we use a custom header 'X-BFY-Content-Encoding' to indicate that
 * the body is compressed, and handle decompression in the backend accordingly.
 * 
 * Not that the 'Content-Type' header should be set to application/octet-stream
 * when using this function to indicate that the body is binary data.
 */
export function useCompressedFetch() {
  const compressedFetch = useCallback(
    async (url: string, options: RequestInit = {}): Promise<Response> => {
      const { body, headers = {}, ...rest } = options;

      // No body → nothing to compress
      if (!body) {
        return fetch(url, options);
      }

      if (typeof body !== "string") {
        throw new Error("Only string body is supported for compressedFetch");
      }

      // Normalize headers
      const finalHeaders =
        headers instanceof Headers ? headers : new Headers(headers);

      const encoded = new TextEncoder().encode(body);
      const payload = gzipSync(encoded);
      finalHeaders.set("X-BFY-Content-Encoding", "gzip");

      return fetch(url, {
          ...rest,
          body: payload as BufferSource,
          headers: finalHeaders,
      });
    },
    []
  );

  return compressedFetch;
}
